import { useFixedNavbar } from '@nrdy-marketing-engine/use-fixed-navbar';
import clsx from 'clsx';
import { useMemo } from 'react';
import { HelpPage } from '../@types/help-page';

const NON_ALPHA_REGEX = /^[^A-Za-z]/;
const CHARACTERS = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

interface HelpPagesIndexProps {
  helpPages: HelpPage[];
}

interface IndexedHelpPage {
  character: string;
  helpPages: HelpPage[];
}

const helpPageStartsWith = (helpPage: HelpPage, character: string) => {
  const isNumberCheck = character === '#';
  if (isNumberCheck) return helpPage.title.match(NON_ALPHA_REGEX);

  return helpPage.title.toLocaleUpperCase().startsWith(character);
};

const anyHelpPageStartsWith = (helpPages: HelpPage[], character: string) =>
  helpPages.some((helpPage) => helpPageStartsWith(helpPage, character));

const firstCharactersForHelpPages = (helpPages: HelpPage[]) =>
  CHARACTERS.filter((character) => anyHelpPageStartsWith(helpPages, character));

const helpPagesGroupedByFirstCharacter = (helpPages: HelpPage[]) => {
  const characters = firstCharactersForHelpPages(helpPages);
  return characters.map((character) => ({
    character,
    helpPages: helpPages.filter((helpPage) => helpPageStartsWith(helpPage, character)),
  }));
};

/**
 * Takes an array of HelpPages, and returns an index by grouping them by first character.
 *
 *     [ pageOne, pageTwo => [{ character: 'A', pages: [ ...] }, ... ]
 */
const useIndexedHelpPages = (helpPages: HelpPage[]) =>
  useMemo<IndexedHelpPage[]>(() => helpPagesGroupedByFirstCharacter(helpPages), [helpPages]);

function LetterLinks({ indexedHelpPages }: { indexedHelpPages: IndexedHelpPage[] }) {
  const isFixedNavbar = useFixedNavbar();

  // '\00b7' is the unicode reference for a bullet point
  return (
    <ul
      className={clsx(
        "text-sm lg:text-lg flex flex-wrap mb-2 py-2 lg:[&>li:not(:last-child)]:after:content-['\\00b7'] lg:[&>li:not(:last-child)]:after:inline -ml-2 bg-background",
        isFixedNavbar && 'sticky top-navbar-height'
      )}
    >
      {indexedHelpPages.map(({ character }) => (
        <li key={character}>
          <a
            className="text-link hover:underline font-bold px-2 py-1"
            href={`#concept-review-topics-${character.toLocaleLowerCase()}`}
          >
            {character}
          </a>
        </li>
      ))}
    </ul>
  );
}

function HelpPages({ helpPages }: { helpPages: HelpPage[] }) {
  return (
    <div
      className="md:columns-2 xl:columns-3 gap-6 group mb-4
      [&>a]:break-inside-avoid [&>a]:-mx-2 [&>a]:block [&>a]:text-link [&>a]:p-2 [&>a:hover]:underline"
    >
      {helpPages.map((page) => (
        <a href={`/hotmath/${page.slug}`} key={page.slug}>
          {page.title}
        </a>
      ))}
    </div>
  );
}

function IndexedHelpPages({ character, helpPages }: { character: string; helpPages: HelpPage[] }) {
  return (
    <>
      <a
        className="top-[-132px] lg:top-[-112px] block relative"
        id={`concept-review-topics-${character.toLocaleLowerCase()}`}
      />
      <h2 className="text-2xl py-2 mb-4 border-b-gray-200 border-b">{character}</h2>
      <HelpPages helpPages={helpPages} />
    </>
  );
}

export default function HelpPagesIndex({ helpPages }: HelpPagesIndexProps) {
  const indexedHelpPages = useIndexedHelpPages(helpPages);
  return (
    <div className="container-xl py-8 md:py-12 xl:py-18">
      <h1 className="text-3xl mb-2">Concept Review Topics</h1>
      <LetterLinks indexedHelpPages={indexedHelpPages} />
      {indexedHelpPages.map(({ character, helpPages }) => (
        <IndexedHelpPages character={character} helpPages={helpPages} key={character} />
      ))}
    </div>
  );
}
